import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { createUser, getMyUsers, deleteUser } from '../services/api/userService';
import { BANK_NAMES, getUserTypeOptions, showCredentialFields } from '../utils/constants';
import { User } from '../types';
import Navbar from '../components/Navbar/Navbar';
import ConfirmationModal from '../components/ConfirmationModal';
import { useTranslation } from '../hooks/useTranslation';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ModuleRegistry, AllCommunityModule, ICellRendererParams } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import './SystemUsersPage.css';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);


const SystemUsersPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { userId, userType, bankName } = useSelector((state: RootState) => state.auth);
  const currentUserType = userType || '';
  
  const [formData, setFormData] = useState({
    userType: 'PARTNER_EMPLOYEE',
    username: '',
    password: '',
    bankName: bankName || 'BFI'
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [deletingUserId, setDeletingUserId] = useState<number | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState<{ id: number; name: string } | null>(null);

  // Only TWIZE_ADMIN can select bank, others use their own bank
  const showBankSelector = currentUserType === 'TWIZE_ADMIN';

  // Load users on component mount
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    if (!userId) return;
    
    setLoadingUsers(true);
    const result = await getMyUsers(userId);
    if (result.success && result.data) {
      // For TWIZE_ADMIN, show all user types except CONSUMER (consumers are in separate page)
      // For others, show only PARTNER_EMPLOYEE users
      const systemUsers = currentUserType === 'TWIZE_ADMIN' 
        ? result.data.filter((user: User) => user.userType !== 'CONSUMER')
        : result.data.filter((user: User) => user.userType === 'PARTNER_EMPLOYEE');
      setUsers(systemUsers);
    }
    setLoadingUsers(false);
  };

  const handleChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!userId) {
      setError(t('systemUsers.messages.userIdNotFound'));
      return;
    }

    // Validate username and password for non-CONSUMER types
    const isConsumer = formData.userType === 'CONSUMER';
    if (!isConsumer) {
      if (!formData.username || formData.username.trim() === '') {
        setError(t('systemUsers.messages.usernameRequired'));
        return;
      }
      if (!formData.password || formData.password.length < 8) {
        setError(t('systemUsers.messages.passwordRequired'));
        return;
      }
    }
    
    setLoading(true);

    // Generate NNI from username or user type (backend requires non-null NNI)
    const generatedNNI = isConsumer 
      ? `CONS-${Date.now().toString().slice(-8)}`
      : `SYS-${formData.username.toUpperCase()}`;
    
    const userData = {
      firstName: isConsumer ? 'Consumer' : formData.username,
      lastName: 'System User',
      nni: generatedNNI,
      phoneNumber: '+000 00 00 00 00',
      availableBalance: 0.00,
      userType: formData.userType, // Use selected user type
      username: isConsumer ? '' : formData.username,
      password: isConsumer ? '' : formData.password,
      bankName: formData.bankName,
      createdById: userId // ID of the logged-in user creating this system user
    };

    console.log('📤 Sending user data to backend:', userData);

    const result = await createUser(userData, userId);
    setLoading(false);

    if (result.success) {
      const userTypeLabel = getUserTypeLabel(formData.userType);
      const userName = isConsumer ? t('userTypes.consumer') : formData.username;
      setSuccess(t('systemUsers.messages.userCreated', { userType: userTypeLabel, userName }));
      // Reset form (keep the user's bank and user type)
      setFormData({
        userType: formData.userType,
        username: '',
        password: '',
        bankName: bankName || 'BFI'
      });
      
      // Reload users list
      loadUsers();
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(''), 5000);
    } else {
      setError(result.message || t('systemUsers.messages.createFailed'));
    }
  };

  const handleDeleteUser = (userIdToDelete: number, userName: string) => {
    setUserToDelete({ id: userIdToDelete, name: userName });
    setShowDeleteConfirm(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    if (!userId) {
      setError(t('systemUsers.messages.userIdNotFound'));
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      return;
    }

    setDeletingUserId(userToDelete.id);
    setError('');
    setSuccess('');

    const result = await deleteUser(userToDelete.id.toString(), userId);

    if (result.success) {
      setSuccess(t('systemUsers.messages.userDeleted', { userName: userToDelete.name }));
      // Reload users list
      loadUsers();
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(''), 5000);
    } else {
      setError(result.message || t('systemUsers.messages.deleteFailed'));
    }

    setDeletingUserId(null);
    setShowDeleteConfirm(false);
    setUserToDelete(null);
  };

  const getUserTypeLabel = (userType?: string) => {
    switch (userType) {
      case 'TWIZE_ADMIN': return t('userTypes.systemAdmin');
      case 'PARTNER_MANAGER': return t('userTypes.bankManager');
      case 'PARTNER_EMPLOYEE': return t('userTypes.bankEmployee');
      case 'CONSUMER': return t('userTypes.consumer');
      default: return userType || '-';
    }
  };

  const columnDefs: ColDef[] = useMemo(() => {
    // Action button component for AG Grid
    const DeleteButtonCell = (params: any) => {
      const user = params.data;
      const isDeleting = deletingUserId === user.id;
      
      return (
        <button
          className="btn-delete"
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteUser(user.id, user.username || `User ${user.id}`);
          }}
          disabled={isDeleting || loading}
        >
          {isDeleting ? t('systemUsers.table.deleting') : t('systemUsers.table.delete')}
        </button>
      );
    };

    return [
    {
      field: 'username',
      headerName: t('systemUsers.table.username'),
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      sortable: true,
      cellRenderer: (params: any) => params.value || '-',
      cellStyle: { fontWeight: 'bold' } as any
    },
    {
      field: 'bankName',
      headerName: t('systemUsers.table.bank'),
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      sortable: true,
      cellRenderer: (params: any) => params.value || '-'
    },
    {
      field: 'userType',
      headerName: t('systemUsers.table.userType'),
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      sortable: true,
      cellRenderer: (params: any) => {
        return React.createElement('span', { className: 'user-type-badge' }, getUserTypeLabel(params.value));
      }
    },
    {
      field: 'actions',
      headerName: t('systemUsers.table.actions'),
      sortable: false,
      filter: false,
      cellRenderer: DeleteButtonCell,
      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' } as any
    }
    ];
  }, [deletingUserId, loading, handleDeleteUser]);

  const defaultColDef = useMemo(() => ({
    resizable: true,
    flex: 1,
    minWidth: 100
  }), []);

  return (
    <div className="system-users-page">
      <Navbar />
      <div className="system-users-content">
        <div className="system-users-header">
          <button className="back-btn" onClick={() => navigate(-1)}>
            ← {t('common.back')}
          </button>
          <h1>{t('systemUsers.title')}</h1>
          <p>
            {currentUserType === 'TWIZE_ADMIN' 
              ? t('systemUsers.description.admin')
              : t('systemUsers.description.employee')}
          </p>
        </div>

        {/* Global Messages */}
        {error && (
          <div className="message error-message">
            {error}
          </div>
        )}

        {success && (
          <div className="message success-message">
            {success}
          </div>
        )}

        {/* Create System User Form */}
        <div className="create-user-section">
          <h2>{t('systemUsers.createUser.title')}</h2>
          <div className="form-container">
            <form onSubmit={handleSubmit} className="user-form">
              {showBankSelector ? (
                <div className="form-row">
                  <div className="form-group full-width">
                    <label htmlFor="bankName">{t('systemUsers.createUser.bankRequired')}</label>
                    <select
                      id="bankName"
                      value={formData.bankName}
                      onChange={(e) => handleChange('bankName', e.target.value)}
                      required
                      disabled={loading}
                    >
                      {BANK_NAMES.map(bank => (
                        <option key={bank.value} value={bank.value}>{bank.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : (
                <div className="form-row">
                  <div className="form-group full-width">
                    <label>{t('systemUsers.createUser.bank')}</label>
                    <div className="bank-display">
                      {bankName || t('common.na')}
                    </div>
                    <small style={{ color: '#718096', marginTop: '4px', display: 'block' }}>
                      {t('systemUsers.createUser.bankHint')}
                    </small>
                  </div>
                </div>
              )}

              {/* User Type Selector - Only for TWIZE_ADMIN */}
              {currentUserType === 'TWIZE_ADMIN' && (
                <div className="form-row">
                  <div className="form-group full-width">
                    <label htmlFor="userType">{t('systemUsers.createUser.userTypeRequired')}</label>
                    <select
                      id="userType"
                      value={formData.userType}
                      onChange={(e) => handleChange('userType', e.target.value)}
                      required
                      disabled={loading}
                    >
                      {getUserTypeOptions(currentUserType).map(option => (
                        <option key={option.value} value={option.value}>{getUserTypeLabel(option.value)}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Username and Password - Only for non-CONSUMER types */}
              {showCredentialFields(formData.userType) && (
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="username">{t('systemUsers.createUser.usernameRequired')}</label>
                    <input
                      id="username"
                      type="text"
                      placeholder={t('systemUsers.createUser.usernamePlaceholder')}
                      value={formData.username}
                      onChange={(e) => handleChange('username', e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="password">{t('systemUsers.createUser.passwordRequired')}</label>
                    <div className="password-input-wrapper">
                      <input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder={t('systemUsers.createUser.passwordPlaceholder')}
                        value={formData.password}
                        onChange={(e) => handleChange('password', e.target.value)}
                        required
                        minLength={8}
                        disabled={loading}
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={loading}
                        aria-label={showPassword ? t('systemUsers.createUser.hidePassword') : t('systemUsers.createUser.showPassword')}
                      >
                        {showPassword ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                            <line x1="1" y1="1" x2="23" y2="23"/>
                          </svg>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                            <circle cx="12" cy="12" r="3"/>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* For CONSUMER, show a message that credentials are not needed */}
              {formData.userType === 'CONSUMER' && (
                <div className="form-row">
                  <div className="form-group full-width">
                    <div style={{ 
                      padding: '12px', 
                      background: '#e3f2fd', 
                      borderRadius: '6px', 
                      color: '#1976d2',
                      fontSize: '14px'
                    }}>
                      {t('systemUsers.createUser.consumerNote')}
                    </div>
                  </div>
                </div>
              )}

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => navigate(-1)}
                  disabled={loading}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? t('systemUsers.createUser.creating') : `${t('systemUsers.createUser.create')} ${getUserTypeLabel(formData.userType)}`}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Users Table */}
        <div className="users-table-section">
          <h2>
            {currentUserType === 'TWIZE_ADMIN' 
              ? t('systemUsers.table.title.admin')
              : t('systemUsers.table.title.employee')}
          </h2>
          <div className="table-container ag-theme-quartz modern-ag-theme" style={{ height: '500px', width: '100%' }}>
            {loadingUsers ? (
              <div className="loading-message">{t('systemUsers.table.loading')}</div>
            ) : users.length === 0 ? (
              <div className="empty-message">{t('systemUsers.table.empty')}</div>
            ) : (
              <AgGridReact
                rowData={users}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                pagination={true}
                paginationPageSize={20}
                suppressCellFocus={true}
                domLayout="normal"
              />
            )}
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        title={t('systemUsers.messages.deleteConfirm')}
        message={userToDelete ? t('systemUsers.messages.deleteMessage', { userName: userToDelete.name }) : ''}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        onConfirm={confirmDeleteUser}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setUserToDelete(null);
        }}
        variant="danger"
      />
    </div>
  );
};

export default SystemUsersPage;

